import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { Icon, type CupertinoIconName } from '../icons/Icon';
import { Tooltip } from './Tooltip';
import styles from './IconButton.module.css';

export interface IconButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
  icon?: CupertinoIconName;
  /** Accessible name; also the tooltip text (Flutter `tooltip`). */
  label: string;
  /** Glyph size (default 24). */
  iconSize?: number;
  /** Glyph colour (default currentColor / onSurface). */
  color?: string;
  /** Hit area in px (Material 48). */
  size?: number;
  /** Visual state-layer diameter (Material 40). */
  visualSize?: number;
  /** `standard` (transparent) or `tonal` (secondaryContainer circle, IconButton.filledTonal). */
  variant?: 'standard' | 'tonal';
  /** Show the label as a tooltip on long-press / hover (default true). */
  tooltip?: boolean;
  /** Custom content instead of `icon`. */
  children?: ReactNode;
  iconStyle?: CSSProperties;
}

/** Material IconButton: 48 px target, 40 px circular state layer, 24 px Cupertino glyph. */
export function IconButton({
  icon,
  label,
  iconSize = 24,
  color,
  size = 48,
  visualSize = 40,
  variant = 'standard',
  tooltip = true,
  children,
  iconStyle,
  className,
  style,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const button = (
    <button
      type={type}
      aria-label={label}
      className={[styles.button, variant === 'tonal' ? styles.tonal : '', className ?? ''].filter(Boolean).join(' ')}
      style={{ width: size, height: size, color, ['--visual-size' as string]: `${visualSize}px`, ...style }}
      {...rest}
    >
      <span className={styles.layer} aria-hidden />
      {children ?? (icon ? <Icon name={icon} size={iconSize} style={iconStyle} /> : null)}
    </button>
  );
  return tooltip ? <Tooltip message={label}>{button}</Tooltip> : button;
}
